"use strict";
/* eslint-disable no-use-before-define */
const { operatorIds, variableIds, operandIds, } = require('@whispir/expression-helper');
const { object, string, array, lazy } = require('../utils/modifiedYup');
const validationOptions = require('../utils/validationOptions');
const operatorIdsArray = Object.values(operatorIds);
const variableIdsArray = Object.values(variableIds);
const { VARIABLE, CONSTANT } = operandIds;
const { BETWEEN, HAS_PASSED, IN_THE_NEXT } = operatorIds;
const operatorSchema = string().oneOf(operatorIdsArray);
const valueTypeSchema = string().oneOf(variableIdsArray);
const variableOperandSchema = object().shape({
    type: string().matches(new RegExp(VARIABLE)).required(),
    stepId: string().required(),
    variableId: string().required(),
});
const baseOperandSchema = object().shape({
    type: string().matches(new RegExp(CONSTANT)).required(),
    /* `value` is always string
       `valueType` indicates the JS primitive type, ie. string/number/date
    */
    value: string().required(),
    valueType: valueTypeSchema.required(),
    context: object().notRequired(),
});
const betweenNumberOperandSchema = baseOperandSchema
    .shape({
    context: object()
        .shape({
        lessThan: baseOperandSchema,
    })
        .required(),
})
    .test({
    name: 'between-number',
    message: '`greaterThan` number must be greater than `lessThan` number',
    test: (operand) => {
        const greaterThanNumber = operand && operand.value;
        const lessThanNumber = operand &&
            operand.context &&
            operand.context.lessThan &&
            operand.context.lessThan.value;
        // value is stored as string by default, parsing is required for value comparison.
        const formattedGreaterThanNumber = parseInt(greaterThanNumber, 10);
        const formattedlessThanNumber = parseInt(lessThanNumber, 10);
        return formattedGreaterThanNumber < formattedlessThanNumber;
    },
});
const betweenDateOperandSchema = baseOperandSchema
    .shape({
    context: object()
        .shape({
        before: baseOperandSchema,
    })
        .required(),
})
    .test({
    name: 'between-date',
    message: '`after` date must be prior to `before` date',
    test: (operand) => {
        const afterValue = operand && operand.value;
        const beforeValue = operand &&
            operand.context &&
            operand.context.before &&
            operand.context.before.value;
        const afterValueNumber = Date.parse(afterValue);
        const beforeValueNumber = Date.parse(beforeValue);
        return afterValueNumber < beforeValueNumber;
    },
});
const withinRangeDateOperandSchema = baseOperandSchema
    .shape({
    context: object()
        .shape({
        durationUnit: string().required(),
    })
        .required(),
})
    .test({
    name: 'within-range-date',
    message: 'duration value must be 1 or greater',
    test: (operand) => {
        const durationValue = operand && operand.value;
        return parseInt(durationValue, 10) >= 1;
    },
});
const constantOperandSchema = baseOperandSchema
    .when(['operator'], {
    is: (operator) => operator === BETWEEN,
    then: object().oneOfSchemas([
        betweenNumberOperandSchema,
        betweenDateOperandSchema,
    ]),
})
    .when('operator', {
    is: (operator) => [HAS_PASSED, IN_THE_NEXT].includes(operator),
    then: withinRangeDateOperandSchema,
    otherwise: baseOperandSchema,
});
const leafExpressionSchema = object()
    .shape({
    type: string().required().oneOf(['expression']),
    id: string().required(),
    operator: operatorSchema.required(),
    left: variableOperandSchema.required(),
    right: constantOperandSchema.required(),
})
    .noUnknown();
const compositeExpressionSchema = object()
    .shape({
    type: string().required().oneOf(['expression']),
    id: string().required(),
    operator: operatorSchema.required(),
    operands: array()
        .of(lazy(() => anyExpressionSchema.default(undefined)))
        .min(1),
})
    .noUnknown();
const anyExpressionSchema = object()
    .shape({
    type: string().required().oneOf(['expression']),
    id: string().required(),
    operator: operatorSchema.required(),
})
    .test({
    test: (expression) => {
        if (expression.left || expression.right) {
            return leafExpressionSchema.validateSync(expression, validationOptions);
        }
        return compositeExpressionSchema.validateSync(expression, validationOptions);
    },
});
exports.expressionSchema = object()
    .shape({
    stepName: string().required().oneOf(['IF Condition']),
    expression: anyExpressionSchema,
})
    .noUnknown();
//# sourceMappingURL=expressionSchema.js.map